import { useMemo } from "react";

export type AdsSlotSchemaEntry = {
  id: string;
  label: string;
  hint?: string;
  max_chars?: number;
};

function normalizeSlotsSchema(raw: unknown): AdsSlotSchemaEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: AdsSlotSchemaEntry[] = [];
  for (const x of raw) {
    if (!x || typeof x !== "object") continue;
    const o = x as Record<string, unknown>;
    const id = typeof o.id === "string" ? o.id.trim() : "";
    if (!id) continue;
    const labelBase = typeof o.label === "string" ? o.label.trim() : "";
    const label = labelBase || id;
    const hint = typeof o.hint === "string" ? o.hint.trim() : undefined;
    const maxRaw = o.max_chars;
    const max_chars =
      typeof maxRaw === "number" && Number.isFinite(maxRaw) && maxRaw >= 0
        ? Math.floor(maxRaw)
        : undefined;
    out.push({ id, label, ...(hint ? { hint } : {}), ...(max_chars !== undefined ? { max_chars } : {}) });
  }
  return out;
}

export function slotsSchemaFromTemplate(slots_schema: unknown): AdsSlotSchemaEntry[] {
  return normalizeSlotsSchema(slots_schema);
}

export function slotIdsFromSlotsSchema(slots_schema: unknown): string[] {
  return slotsSchemaFromTemplate(slots_schema).map((s) => s.id);
}

function parseSlotObject(text: string): { ok: true; value: Record<string, unknown> } | { ok: false } {
  const t = text.trim();
  if (!t) return { ok: true, value: {} };
  try {
    const j = JSON.parse(t) as unknown;
    if (!j || typeof j !== "object" || Array.isArray(j)) return { ok: false };
    return { ok: true, value: j as Record<string, unknown> };
  } catch {
    return { ok: false };
  }
}

function valueToString(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return "";
  }
}

export function AdsAssistantSlotFillFields(props: {
  slots: AdsSlotSchemaEntry[];
  slotFillJsonText: string;
  setSlotFillJsonText: (v: string) => void;
  disabled?: boolean;
}) {
  const { slots, slotFillJsonText, setSlotFillJsonText, disabled } = props;

  const parsed = useMemo(() => parseSlotObject(slotFillJsonText), [slotFillJsonText]);

  function commit(nextObj: Record<string, unknown>) {
    const keys = Object.keys(nextObj);
    setSlotFillJsonText(keys.length ? JSON.stringify(nextObj, null, 2) : "");
  }

  function onFieldChange(slotId: string, text: string, maxChars?: number) {
    const base = parsed.ok ? { ...parsed.value } : {};
    let v = text;
    if (maxChars !== undefined && v.length > maxChars) v = v.slice(0, maxChars);
    if (!v.trim()) delete base[slotId];
    else base[slotId] = v;
    commit(base);
  }

  if (!slots.length) {
    return (
      <p className="text-[11px] text-gray-500 dark:text-gray-400">
        Pick a template with slots to edit hook, body, CTA, and other fields here.
      </p>
    );
  }

  if (!parsed.ok) {
    return (
      <p className="text-[11px] text-amber-800 dark:text-amber-200/90">
        Slot fills are not valid JSON. Clear the field or fix braces and quotes to use labeled inputs.
      </p>
    );
  }

  const obj = parsed.value;

  return (
    <div className="space-y-2">
      {slots.map((s) => {
        const raw = obj[s.id];
        const str = valueToString(raw);
        const useTextarea = (s.max_chars ?? 0) > 140 || str.length > 80;
        const common = "mt-0.5 w-full rounded border border-gray-200 bg-white px-2 py-1 text-sm dark:border-gray-600 dark:bg-zinc-950 dark:text-gray-100";
        return (
          <label key={s.id} className="block text-[11px] text-gray-700 dark:text-gray-300">
            <span className="font-medium text-gray-900 dark:text-gray-100">{s.label}</span>
            {s.hint ? (
              <span className="ml-1 font-normal text-gray-500 dark:text-gray-500">({s.hint})</span>
            ) : null}
            {useTextarea ? (
              <textarea
                className={`${common} min-h-[52px]`}
                value={str}
                disabled={disabled}
                maxLength={s.max_chars}
                onChange={(e) => onFieldChange(s.id, e.target.value, s.max_chars)}
              />
            ) : (
              <input
                type="text"
                className={common}
                value={str}
                disabled={disabled}
                maxLength={s.max_chars}
                onChange={(e) => onFieldChange(s.id, e.target.value, s.max_chars)}
              />
            )}
          </label>
        );
      })}
    </div>
  );
}
